import { useNavigate } from 'react-router-dom';
import { CTABanner, SectionHeader, Button } from '../components/ui/UI';
import ContactInfo from '../components/ui/ContactInfo';
import { SERVICES } from '../data/services';
import './Education.css';

const COURSE_CATEGORIES = [
  {
    title: 'Aviation & Hospitality',
    icon: '✈️',
    color: 'saffron',
    courses: ['Air Hostess & Ground Staff Course', 'Pilot Training Academy', 'Hotel Management Course', 'Private Jet Air Hostess Course'],
  },
  {
    title: 'Medical & Healthcare',
    icon: '🏥',
    color: 'teal',
    courses: ['MBBS / BAMS / BDS Programs', 'Paramedical Courses', 'Nursing GNM', 'Radiology & MLT'],
  },
  {
    title: 'Management & Business',
    icon: '🎓',
    color: 'gold',
    courses: ['MBA / BBA Admissions', 'Engineering (B.Tech / M.Tech)', 'Distance Education MBA', 'Part-time MBA'],
  },
  {
    title: 'Computer & IT',
    icon: '💻',
    color: 'navy',
    courses: ['Web Design & Development', 'Graphics Design & Photoshop', 'Data Entry & MS Office', 'Tally / SAP / Accounting'],
  },
  {
    title: 'Creative & Lifestyle',
    icon: '🎨',
    color: 'saffron',
    courses: ['Modelling & Acting Academy', 'Interior Design Course', 'Fashion Designing', 'Beauty & Mehndi Classes'],
  },
  {
    title: 'Study Abroad',
    icon: '🌍',
    color: 'teal',
    courses: ['Study in Singapore / Germany / UK', 'Study in UAE / Japan / Russia', 'IELTS / TOEFL Guidance', 'University Admission Help'],
  },
];

const COUNTRIES_STUDY = [
  { flag: '🇸🇬', name: 'Singapore' },
  { flag: '🇩🇪', name: 'Germany' },
  { flag: '🇬🇧', name: 'UK' },
  { flag: '🇺🇸', name: 'USA' },
  { flag: '🇯🇵', name: 'Japan' },
  { flag: '🇷🇺', name: 'Russia' },
  { flag: '🇦🇪', name: 'UAE' },
  { flag: '🇫🇷', name: 'France' },
  { flag: '🇮🇹', name: 'Italy' },
  { flag: '🇦🇺', name: 'Australia' },
  { flag: '🇨🇦', name: 'Canada' },
  { flag: '🇰🇿', name: 'Kazakhstan' },
];

const educationServices = SERVICES.filter(s => s.cat === 'education');

// Optimized Course Card for Education Page
function EduCourseCard({ course, onInquire }) {
  return (
    <div className={`edu-course-card edu-course-card--${course.color}`}>
      <div className="edu-course-card__top-bar" />
      <div className="edu-course-card__inner">
        <div className="edu-course-card__header">
          <div className="edu-course-card__icon-box">{course.icon}</div>
          <span className="edu-course-card__tag">Job Oriented</span>
        </div>

        <h3 className="edu-course-card__title">{course.title}</h3>
        <p className="edu-course-card__desc">{course.short}</p>

        <div className="edu-course-card__features">
          {course.features && course.features.slice(0, 3).map(f => (
            <div key={f} className="edu-course-card__feat-item">
              <span className="feat-check">✓</span>
              {f}
            </div>
          ))}
        </div>

        <button
          className="edu-course-card__cta"
          onClick={() => onInquire(course.title)}
        >
          Check Course Details
        </button>
      </div>
    </div>
  );
}

export default function Education() {
  const navigate = useNavigate();

  const handleInquire = (serviceTitle) => {
    navigate('/contact', { state: { service: serviceTitle } });
  };

  return (
    <div className="page-wrapper education-page">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="education-hero">
        <div className="education-hero__bg" />
        <div className="container education-hero__inner">
          <div className="education-hero__grid">
            <div className="education-hero__content animate-fade-up">
              <span className="section-badge badge-white">Education & Career</span>
              <h1 className="education-hero__title">
                Build Your Future<br />
                <span className="education-hero__highlight">With Global Skills</span>
              </h1>
              <p className="education-hero__sub">
                Professional training and university admissions for students in Mumbai bombay international worldwide and internationally. 100+ courses with 100% placement assistance.
              </p>
              <div className="education-hero__actions">
                <Button to="/contact" size="lg" state={{ service: 'Admission Inquiry' }}>Apply for Admission</Button>
                <Button variant="outline-white" size="lg" to="/contact" state={{ service: 'Study Abroad' }}>Study Abroad Help</Button>
              </div>

              <div className="education-hero__features">
                <div className="edu-feat-item">
                  <span className="edu-feat-icon">🎓</span>
                  <span>100+ Courses</span>
                </div>
                <div className="edu-feat-item">
                  <span className="edu-feat-icon">🌍</span>
                  <span>50+ Countries</span>
                </div>
                <div className="edu-feat-item">
                  <span className="edu-feat-icon">💼</span>
                  <span>Placements</span>
                </div>
              </div>
            </div>

            <div className="education-hero__visual animate-fade-in delay-2">
              <div className="edu-visual-card edu-visual-card--1">
                <div className="edu-visual-card__img">✈️</div>
                <div className="edu-visual-card__text">
                  <strong>Aviation</strong>
                  <span>Training Programs</span>
                </div>
              </div>
              <div className="edu-visual-card edu-visual-card--2">
                <div className="edu-visual-card__img">🏥</div>
                <div className="edu-visual-card__text">
                  <strong>Medical</strong>
                  <span>Global Degrees</span>
                </div>
              </div>
              <div className="edu-visual-card edu-visual-card--3">
                <div className="edu-visual-card__img">💻</div>
                <div className="edu-visual-card__text">
                  <strong>Technology</strong>
                  <span>Coding & Design</span>
                </div>
              </div>
              <div className="edu-visual-card edu-visual-card--4">
                <div className="edu-visual-card__img">🌎</div>
                <div className="edu-visual-card__text">
                  <strong>Study Abroad</strong>
                  <span>Visa & Intake</span>
                </div>
              </div>
              <div className="education-hero__deco-circle" />
            </div>
          </div>
        </div>
      </section>

      {/* Course category overview */}
      <section className="section-pad">
        <div className="container">
          <SectionHeader
            badge="Categories"
            title="What Would You Like to Study?"
            subtitle="Explore our specialized training domains in Mumbai bombay international worldwide. Select a course to start your journey."
          />
          <div className="edu-cat-grid">
            {COURSE_CATEGORIES.map((cat, i) => (
              <div key={cat.title} className={`edu-cat-card edu-cat-card--${cat.color} animate-fade-up delay-${(i % 3) + 1}`}>
                <div className="edu-cat-card__header">
                  <span className="edu-cat-card__icon">{cat.icon}</span>
                  <h3 className="edu-cat-card__title">{cat.title}</h3>
                </div>
                <ul className="edu-cat-card__list">
                  {cat.courses.map(course => (
                    <li key={course}>
                      <button
                        className="edu-cat-card__course-btn"
                        onClick={() => navigate('/contact', { state: { service: course } })}
                      >
                        {course}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Abroad Section */}
      <section className="section-pad edu-abroad">
        <div className="container">
          <div className="edu-abroad__grid">
            <div className="edu-abroad__text">
              <span className="section-badge badge-white">Worldwide Placements</span>
              <h2 className="edu-abroad__title">
                Study in Top<br /><span>Universities</span>
              </h2>
              <p className="edu-abroad__sub">
                End-to-end guidance for international education from Mumbai bombay international worldwide. We handle everything from applications to visas and post-landing support.
              </p>
              <ul className="edu-abroad__benefits">
                {[
                  '100% Visa Assistance',
                  'Top University Selection',
                  'Scholarship Guidance',
                  'Accommodation Support',
                ].map(b => (
                  <li key={b} className="edu-abroad__benefit">
                    <span className="edu-abroad__check">✓</span> {b}
                  </li>
                ))}
              </ul>
              <Button
                variant="white"
                onClick={() => navigate('/contact', { state: { service: 'Study Abroad Program' } })}
              >
                Inquire Study Abroad
              </Button>
            </div>
            <div className="edu-abroad__countries">
              <div className="edu-abroad__country-grid">
                {COUNTRIES_STUDY.map(c => (
                  <div key={c.name} className="edu-abroad__country-card">
                    <span className="edu-abroad__country-flag">{c.flag}</span>
                    <span className="edu-abroad__country-name">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Education Services - THE REDESIGNED CARDS */}
      <section className="section-pad">
        <div className="container">
          <SectionHeader
            badge="Programs"
            title="Explore Professional Courses"
            subtitle="Industry-verified curriculum in Mumbai bombay international worldwide designed to make you job-ready from day one."
          />
          <div className="edu-services-grid">
            {educationServices.map((s, i) => (
              <div key={s.id} className={`animate-fade-up delay-${(i % 3) + 1}`}>
                <EduCourseCard course={s} onInquire={handleInquire} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission process */}
      <section className="section-pad edu-process">
        <div className="container">
          <SectionHeader
            badge="Process"
            badgeVariant="teal"
            title="Your Path to Success"
            subtitle="How we help students in Mumbai bombay international worldwide go from inquiry to enrollment in 4 simple steps."
          />
          <div className="edu-steps">
            {[
              { step: '01', title: 'Consultation', desc: 'Free counselling to identify your goals.' },
              { step: '02', title: 'Selection', desc: 'Choosing the right course & institute.' },
              { step: '03', title: 'Application', desc: 'Hassle-free documentation & process.' },
              { step: '04', title: 'Enrollment', desc: 'Start your professional journey.' },
            ].map((s, i) => (
              <div key={s.step} className={`edu-step animate-fade-up delay-${i + 1}`}>
                <div className="edu-step__num">{s.step}</div>
                <h3 className="edu-step__title">{s.title}</h3>
                <p className="edu-step__desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
       </section>

       {/* Contact Section */}
       <section className="section-pad edu-quick-contact">
         <div className="container">
           <div className="edu-quick-contact__inner">
             <h2 className="edu-quick-contact__title">🎓 Questions About Our Programs?</h2>
             <p className="edu-quick-contact__sub">Reach out to our education counselors today. We're available 24/7!</p>
             <ContactInfo layout="compact" theme="light" />
           </div>
         </div>
       </section>

       <CTABanner
         title="Start Your Professional Journey Today"
         sub="Our counselors in Mumbai bombay international worldwide are ready to help you choose the best career path."
       />
    </div>
  );
}
